import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ethers } from 'ethers';

import Alert from './Alert';
import { addLiquidity, loadBalances } from '../store/interactions';
import { getNetworkConfig } from '../utils/network';
import { formatAmount, shortenAddress, friendlyError } from '../utils/format';
import AMM_ABI from '../abis/AMM.json';
import TOKEN_ABI from '../abis/Token.json';

const Deposit = () => {
  const [token1Amount, setToken1Amount] = useState('');
  const [token2Amount, setToken2Amount] = useState('');
  const [targetAmm, setTargetAmm] = useState('amm1');
  const [showAlert, setShowAlert] = useState(false);
  const [userMessage, setUserMessage] = useState('');

  const provider = useSelector((state) => state.provider.connection);
  const account = useSelector((state) => state.provider.account);
  const chainId = useSelector((state) => state.provider.chainId);
  const symbols = useSelector((state) => state.tokens.symbols);
  const amm1Balances = useSelector((state) => state.tokens.amm1Balances);
  const amm2Balances = useSelector((state) => state.tokens.amm2Balances);
  const isDepositing = useSelector((state) => state.amm.depositing.isDepositing);
  const isSuccess = useSelector((state) => state.amm.depositing.isSuccess);
  const transactionHash = useSelector((state) => state.amm.depositing.transactionHash);
  const dispatch = useDispatch();

  const networkConfig = useMemo(() => getNetworkConfig(chainId), [chainId]);

  const poolBalances = targetAmm === 'amm2' ? amm2Balances : amm1Balances;
  const selectedAddress =
    networkConfig &&
    (targetAmm === 'amm2' ? networkConfig.amm2.ammAddress : networkConfig.amm1.ammAddress);

  const dappSym = symbols?.dappToken1Symbol || 'DAPP';
  const usdSym = symbols?.usdToken1Symbol || 'USD';

  const depositHandler = async (e) => {
    e.preventDefault();
    setShowAlert(false);
    setUserMessage('');

    if (!provider || !account || !networkConfig) {
      setUserMessage('Connect wallet on a supported network first.');
      return;
    }

    try {
      const amountA = ethers.utils.parseUnits(token1Amount || '0', 'ether');
      const amountB = ethers.utils.parseUnits(token2Amount || '0', 'ether');
      if (amountA.lte(0) || amountB.lte(0)) {
        setUserMessage('Enter positive amounts for both tokens.');
        return;
      }

      const ammAddress =
        targetAmm === 'amm2' ? networkConfig.amm2.ammAddress : networkConfig.amm1.ammAddress;
      const amm = new ethers.Contract(ammAddress, AMM_ABI, provider);
      const tokenA = new ethers.Contract(
        networkConfig.amm1.dappTokenAddress,
        TOKEN_ABI,
        provider
      );
      const tokenB = new ethers.Contract(
        networkConfig.amm1.usdTokenAddress,
        TOKEN_ABI,
        provider
      );

      await addLiquidity(provider, amm, tokenA, tokenB, amountA, amountB, dispatch);
      await loadBalances(provider, networkConfig, dispatch);
      setShowAlert(true);
    } catch (error) {
      console.error(error);
      setUserMessage(friendlyError(error, 'Deposit failed.'));
      setShowAlert(true);
    }
  };

  const ctaLabel = isDepositing ? 'Depositing…' : 'Add Liquidity';

  return (
    <div style={{ maxWidth: 'var(--content-max)', margin: '0 auto' }}>
      <section className="card card--elevated" aria-labelledby="deposit-title">
        <p className="card__eyebrow">Liquidity</p>
        <h1 id="deposit-title" className="card__title">
          Add Liquidity
        </h1>
        <p className="card__subtitle">
          Demo / testnet liquidity deposit into a single AMM pool. Not production liquidity.
        </p>

        {account && networkConfig ? (
          <form onSubmit={depositHandler}>
            <div className="field">
              <label className="field__label" htmlFor="deposit-amm">
                Selected AMM
              </label>
              <select
                id="deposit-amm"
                className="field__select"
                value={targetAmm}
                onChange={(e) => setTargetAmm(e.target.value)}
              >
                <option value="amm1">AMM 1 · {shortenAddress(networkConfig.amm1.ammAddress)}</option>
                <option value="amm2">AMM 2 · {shortenAddress(networkConfig.amm2.ammAddress)}</option>
              </select>
            </div>

            {poolBalances && (
              <p className="helper-text" style={{ marginTop: 0, marginBottom: 16 }}>
                Pool reserves · {dappSym}{' '}
                <span title={poolBalances.dappTokenBalance}>
                  {formatAmount(poolBalances.dappTokenBalance, 4)}
                </span>
                {' · '}
                {usdSym}{' '}
                <span title={poolBalances.usdTokenBalance}>
                  {formatAmount(poolBalances.usdTokenBalance, 4)}
                </span>
                {selectedAddress && (
                  <>
                    {' · '}
                    <span title={selectedAddress}>{shortenAddress(selectedAddress)}</span>
                  </>
                )}
              </p>
            )}

            <div className="token-panel" style={{ marginBottom: 12 }}>
              <div className="token-panel__top">
                <span className="token-panel__label">Token A</span>
                <span className="token-panel__balance">{dappSym}</span>
              </div>
              <div className="token-panel__row">
                <input
                  className="token-panel__amount"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  placeholder="0.0"
                  aria-label={`${dappSym} amount`}
                  value={token1Amount}
                  onChange={(e) => setToken1Amount(e.target.value)}
                />
                <span className="token-select" style={{ pointerEvents: 'none' }}>
                  {dappSym}
                </span>
              </div>
            </div>

            <div className="token-panel">
              <div className="token-panel__top">
                <span className="token-panel__label">Token B</span>
                <span className="token-panel__balance">{usdSym}</span>
              </div>
              <div className="token-panel__row">
                <input
                  className="token-panel__amount"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  placeholder="0.0"
                  aria-label={`${usdSym} amount`}
                  value={token2Amount}
                  onChange={(e) => setToken2Amount(e.target.value)}
                />
                <span className="token-select" style={{ pointerEvents: 'none' }}>
                  {usdSym}
                </span>
              </div>
            </div>

            {userMessage && !showAlert && (
              <p className="helper-text" role="status" style={{ color: 'var(--warning)' }}>
                {userMessage}
              </p>
            )}

            <div className="btn-row">
              <button
                type="submit"
                className="btn btn--primary"
                disabled={isDepositing}
                aria-busy={isDepositing}
              >
                {isDepositing && <span className="spinner" aria-hidden="true" />}
                {ctaLabel}
              </button>
            </div>
          </form>
        ) : (
          <p className="helper-text">Connect wallet on Sepolia or Localhost to deposit.</p>
        )}
      </section>

      {isDepositing ? (
        <Alert
          message="Depositing…"
          transactionHash={null}
          variant="info"
          setShowAlert={setShowAlert}
        />
      ) : isSuccess && showAlert ? (
        <Alert
          message="Transaction Confirmed"
          transactionHash={transactionHash}
          variant="success"
          setShowAlert={setShowAlert}
        />
      ) : !isSuccess && showAlert ? (
        <Alert
          message={userMessage || 'Transaction Failed'}
          transactionHash={null}
          variant="danger"
          setShowAlert={setShowAlert}
        />
      ) : null}
    </div>
  );
};

export default Deposit;
